import { apiClient } from './apiClient';

export interface Organization {
  id: string;
  name: string;
  admin_email: string;
  support_email?: string;
  created_at: string;
  updated_at: string;
  user_count?: number;
  site_count?: number;
  asset_count?: number;
}

export interface CreateOrganizationRequest {
  name: string;
  admin_email: string;
  support_email?: string;
}

export interface UpdateOrganizationRequest {
  name?: string;
  admin_email?: string;
  support_email?: string;
}

export interface OrganizationMember {
  id: string;
  tenant_id: string;
  user_id: string;
  role: string;
  is_active: boolean;
  invited_by: string;
  joined_at: string;
  updated_at: string;
}

export interface GetMembersParams {
  tenant_id: string;
  _limit?: number;
  _offset?: number;
}

export interface UpdateMemberRequest {
  role?: string;
  is_active?: boolean;
}

export interface OrganizationInvitation {
  id: string;
  tenant_id: string;
  email: string;
  role: string;
  invited_by: string;
  created_at: string;
  expires_at: string;
  accepted_at?: string;
  accepted_by?: string;
}

export interface CreateInvitationRequest {
  email: string;
  role: string;
  expires_in_days?: number;
}

export const organizationApi = apiClient.injectEndpoints({
  endpoints: (builder) => ({
    // List all organizations (for super admins)
    listOrganizations: builder.query<Organization[], void>({
      query: () => ({
        url: '/api/v1/admin/organizations',
        method: 'GET',
      }),
    }),

    // Get organization by ID
    getOrganization: builder.query<Organization, string>({
      query: (tenant_id) => ({
        url: `/api/v1/organizations/${tenant_id}`,
        method: 'GET',
      }),
    }),

    // Create organization (for organization admins)
    createOrganization: builder.mutation<Organization, CreateOrganizationRequest>({
      query: (body) => ({
        url: '/api/v1/admin/organizations',
        method: 'POST',
        body,
      }),
    }),

    // Update organization
    updateOrganization: builder.mutation<Organization, { tenant_id: string; body: UpdateOrganizationRequest }>({
      query: ({ tenant_id, body }) => ({
        url: `/api/v1/organizations/${tenant_id}`,
        method: 'PATCH',
        body,
      }),
    }),

    // Delete organization
    deleteOrganization: builder.mutation<void, string>({
      query: (tenant_id) => ({
        url: `/api/v1/organizations/${tenant_id}`,
        method: 'DELETE',
      }),
    }),

    // List organization members
    getOrganizationMembers: builder.query<OrganizationMember[], GetMembersParams>({
      query: ({ tenant_id, _limit = 100, _offset = 0 }) => ({
        url: `/api/v1/organizations/${tenant_id}/members`,
        method: 'GET',
        params: {
          _limit,
          _offset,
        },
      }),
    }),

    // Get organization member
    getOrganizationMember: builder.query<OrganizationMember, { tenant_id: string; member_id: string }>({
      query: ({ tenant_id, member_id }) => ({
        url: `/api/v1/organizations/${tenant_id}/members/${member_id}`,
        method: 'GET',
      }),
    }),

    // Update organization member
    updateOrganizationMember: builder.mutation<
      OrganizationMember,
      { tenant_id: string; member_id: string; body: UpdateMemberRequest }
    >({
      query: ({ tenant_id, member_id, body }) => ({
        url: `/api/v1/organizations/${tenant_id}/members/${member_id}`,
        method: 'PATCH',
        body,
      }),
    }),

    // Remove organization member
    removeOrganizationMember: builder.mutation<void, { tenant_id: string; member_id: string }>({
      query: ({ tenant_id, member_id }) => ({
        url: `/api/v1/organizations/${tenant_id}/members/${member_id}`,
        method: 'DELETE',
      }),
    }),

    // List organization invitations
    getOrganizationInvitations: builder.query<OrganizationInvitation[], string>({
      query: (tenant_id) => ({
        url: `/api/v1/organizations/${tenant_id}/invitations`,
        method: 'GET',
      }),
    }),

    // Create organization invitation
    createOrganizationInvitation: builder.mutation<
      OrganizationInvitation,
      { tenant_id: string; body: CreateInvitationRequest }
    >({
      query: ({ tenant_id, body }) => ({
        url: `/api/v1/organizations/${tenant_id}/invitations`,
        method: 'POST',
        body,
      }),
    }),

    // Revoke organization invitation
    revokeOrganizationInvitation: builder.mutation<void, { tenant_id: string; invitation_id: string }>({
      query: ({ tenant_id, invitation_id }) => ({
        url: `/api/v1/organizations/${tenant_id}/invitations/${invitation_id}`,
        method: 'DELETE',
      }),
    }),
  }),
  overrideExisting: true,
});

export const {
  useListOrganizationsQuery,
  useGetOrganizationQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useGetOrganizationMembersQuery,
  useGetOrganizationMemberQuery,
  useUpdateOrganizationMemberMutation,
  useRemoveOrganizationMemberMutation,
  useGetOrganizationInvitationsQuery,
  useCreateOrganizationInvitationMutation,
  useRevokeOrganizationInvitationMutation,
} = organizationApi;

